'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Mail, Phone, MapPin, Instagram, Twitter, Youtube, Linkedin } from 'lucide-react';
import { settingsApi, PublicSettings } from '@/lib/api';
import { useTranslations } from 'next-intl';
import { TikTokIcon } from './icons/TikTokIcon';
import { clearAnalyticsConsent } from '@/lib/consent';
import { useCanChangeConsent } from '@/lib/use-consent';

const SOCIAL_ICONS = [
  { key: 'social.instagram', icon: Instagram, label: 'Instagram' },
  { key: 'social.tiktok',    icon: TikTokIcon, label: 'TikTok'    },
  { key: 'social.twitter',   icon: Twitter,   label: 'Twitter'   },
  { key: 'social.youtube',   icon: Youtube,   label: 'Youtube'   },
  { key: 'social.linkedin',  icon: Linkedin,  label: 'LinkedIn'  },
] as const;

const DEFAULTS = {
  email:   'info@aksademia.com',
  phone:   '',
  address: 'Kab. Mesuji, Lampung',
};

export function Footer() {
  // Same width as the navbar. px, not a rem class: `html` is 14px here.
  const containerClass = 'max-w-[1280px]';
  const t = useTranslations('footer');
  const tc = useTranslations('consent');
  const [settings, setSettings] = useState<PublicSettings>({});
  const currentYear = new Date().getFullYear();
  const canChangeConsent = useCanChangeConsent();

  useEffect(() => {
    settingsApi.getPublic().then(setSettings).catch(() => {});
  }, []);

  const email   = String(settings['contact.email']   ?? DEFAULTS.email);
  const phone   = String(settings['contact.phone']   ?? DEFAULTS.phone);
  const address = String(settings['contact.address'] ?? DEFAULTS.address);

  const socialLinks = SOCIAL_ICONS.map(({ key, icon, label }) => ({
    icon,
    label,
    href: String(settings[key] ?? ''),
  }));

  const coursesComingSoon = Boolean(settings['courses.comingSoon.enabled']);

  const footerLinks: Record<string, { label: string; href: string; badge?: boolean }[]> = {
    [t('produk')]: [
      { label: t('links.paketUjian'), href: '/tests' },
      { label: t('links.paketBelajar'), href: '/courses', badge: coursesComingSoon },
      { label: t('links.liveEvents'), href: '/events' },
      { label: t('links.hargaPaket'), href: '/harga' },
    ],
    [t('perusahaan')]: [
      { label: t('links.tentangKami'), href: '/tentang-kami' },
      { label: t('links.blog'), href: '/blog' },
      { label: t('links.karir'), href: '/karir' },
      { label: t('links.pressKit'), href: '/press-kit' },
      { label: t('links.kontakKami'), href: '/kontak' },
    ],
    [t('legal')]: [
      { label: t('links.kebijakanPrivasi'), href: '/kebijakan-privasi' },
      { label: t('links.syaratKetentuan'), href: '/syarat-ketentuan' },
      { label: t('links.kebijakanPengembalian'), href: '/kebijakan-pengembalian' },
      { label: t('links.pusatBantuan'), href: '/help' },
    ],
  };

  return (
    <footer className="bg-pattern bg-brand-deep text-teal-100 border-t border-white/5 [--pattern-color:rgb(255_255_255_/_0.035)]">
      {/* Main Footer */}
      <div className={`${containerClass} mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16`}>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link href="/" className="flex items-center mb-4">
              <span className="text-xl font-bold text-white">
                Aksa<span className="text-teal-400">demia</span>
              </span>
            </Link>
            <p className="mb-4 text-sm leading-relaxed">
              {t('tagline')}
            </p>

            {/* Contact Info */}
            <div className="space-y-2">
              <a href={`mailto:${email}`} className="flex items-center gap-3 text-sm hover:text-white transition-colors">
                <Mail className="w-4 h-4 text-orange-400 shrink-0" />
                {email}
              </a>
              {phone && (
                <a href={`tel:${phone.replace(/[^+\d]/g, '')}`} className="flex items-center gap-3 text-sm hover:text-white transition-colors">
                  <Phone className="w-4 h-4 text-orange-400 shrink-0" />
                  {phone}
                </a>
              )}
              <div className="flex items-center gap-3 text-sm">
                <MapPin className="w-4 h-4 text-orange-400 shrink-0" />
                {address}
              </div>
            </div>
          </div>

          {/* Links Columns */}
          <div className="grid grid-cols-3 gap-4 lg:col-span-3 lg:grid-cols-3 lg:gap-8">
            {Object.entries(footerLinks).map(([category, links]) => (
              <div key={category}>
                <h4 className="text-white font-bold uppercase tracking-wider text-xs lg:text-sm mb-3">
                  {category}
                </h4>
                <ul className="space-y-2">
                  {links.map((link) => (
                    <li key={link.href}>
                      <Link
                        href={link.href}
                        className="hover:text-white transition-colors text-xs lg:text-sm inline-flex items-center gap-1.5"
                      >
                        {link.label}
                        {link.badge && (
                          <span className="bg-amber-400/20 text-amber-300 text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                            {t('segeraBadge')}
                          </span>
                        )}
                      </Link>
                    </li>
                  ))}
                  {category === t('legal') && canChangeConsent && (
                    <li>
                      {/* Forgets the stored choice, which re-opens the consent bar. */}
                      <button
                        type="button"
                        onClick={clearAnalyticsConsent}
                        className="hover:text-white transition-colors text-xs lg:text-sm text-left"
                      >
                        {tc('settingsLink')}
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className={`${containerClass} mx-auto px-4 sm:px-6 lg:px-8 py-6`}>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            {/* Legal entity — registered company name + domicile (KYB / payment-gateway compliance) */}
            <div className="text-center sm:text-left space-y-0.5">
              <p className="text-sm font-medium text-slate-300">{t('businessName')}</p>
              <p className="text-xs text-teal-100">{t('businessAddress')}</p>
              <p className="text-xs text-teal-100" suppressHydrationWarning>{t('copyright', { year: currentYear })}</p>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-3">
              {socialLinks.filter((s) => !!s.href).map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    aria-label={social.label}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-xl bg-white/5 hover:bg-teal-500 flex items-center justify-center hover:text-white transition-all"
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
